// Row contracts and jsonb shapes of agent rooms (M4-05; 06 §Channels "Agent rooms"; 02 §Personal
// crews "Agent rooms"). Same conventions as `rows.ts`: keys are column names, nullability is the
// column's, enums are the CHECK's; bound in `contract-bindings.ts`.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  AgentRoomParticipantKind,
  AgentRoomTurnPolicy,
  RunState,
  SharingRule,
  StatusAskEscalation,
  StatusAskState,
  StatusAskSubjectKind,
} from "./enums";
import { LIMITS, Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

// jsonb shapes ----------------------------------------------------------------------------------

/**
 * `status_ask.answer`: the status, and a note. Under the target crew's `status_only` rule an
 * agent's answer is the status alone (the note is refused); a person may add one.
 */
export const StatusAskAnswer = z
  .strictObject({
    status: z.string().min(1).max(60),
    note: z.string().max(LIMITS.text).nullable(),
  })
  .meta({ id: "StatusAskAnswer" });
export type StatusAskAnswer = z.infer<typeof StatusAskAnswer>;

// Rows ------------------------------------------------------------------------------------------

export const AgentRoom = z
  .object({
    company_id: Uuid,
    id: Uuid,
    channel_id: Uuid,
    topic_id: Uuid.nullable(),
    turn_policy: AgentRoomTurnPolicy,
    window_size: z.int().min(1).max(200),
    max_agent_turns: z.int().min(1).max(50),
    current_turn_membership_id: Uuid.nullable(),
    turn_seq: z.int().min(0),
    turn_started_at: Timestamp.nullable(),
    agent_turns_since_human: z.int().min(0),
    stalled_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AgentRoom" });
export type AgentRoom = z.infer<typeof AgentRoom>;

export const AgentRoomParticipant = z
  .object({
    company_id: Uuid,
    room_id: Uuid,
    membership_id: Uuid,
    kind: AgentRoomParticipantKind,
    turn_order: z.int().min(0),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AgentRoomParticipant" });
export type AgentRoomParticipant = z.infer<typeof AgentRoomParticipant>;

export const AgentRoomParkedRun = z
  .object({
    company_id: Uuid,
    id: Uuid,
    run_id: Uuid,
    channel_id: Uuid,
    topic_id: Uuid.nullable(),
    prior_state: RunState,
    prior_reason: z.string().nullable(),
    prior_lease_owner: z.string().nullable(),
    parked_at: Timestamp,
    parked_by_membership_id: Uuid,
    resumed_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AgentRoomParkedRun" });
export type AgentRoomParkedRun = z.infer<typeof AgentRoomParkedRun>;

export const StatusAsk = z
  .object({
    company_id: Uuid,
    id: Uuid,
    room_id: Uuid,
    asker_membership_id: Uuid,
    asker_run_id: Uuid.nullable(),
    target_membership_id: Uuid,
    subject_kind: StatusAskSubjectKind,
    subject_id: Uuid,
    question: z.string().nullable(),
    rule: SharingRule,
    state: StatusAskState,
    escalation: StatusAskEscalation.nullable(),
    escalated_to_membership_id: Uuid.nullable(),
    escalated_at: Timestamp.nullable(),
    answer: StatusAskAnswer.nullable(),
    answered_by_membership_id: Uuid.nullable(),
    answered_at: Timestamp.nullable(),
    deadline_at: Timestamp,
    ...actor,
    ...stamps,
  })
  .meta({ id: "StatusAsk" });
export type StatusAsk = z.infer<typeof StatusAsk>;
